import React from 'react';
import { AlertTriangle, Info } from 'lucide-react';
import { Badge, Button, Card, ProgressBar, SampleBadge, StatCard } from '../../Components/ui';
import { cx } from '../../Components/ui/utils';
import { DASH } from '../format';
import { sampleAction } from './sampleAction';
import { statusVariant } from './status';

// Holat belgisi (matn bo'yicha rang)
export function StatusBadge({ children, variant }) {
  if (children === null || children === undefined || children === '') return <span className="ui-cab-muted">{DASH}</span>;
  return (
    <Badge variant={variant || statusVariant(children)} size="sm">
      {children}
    </Badge>
  );
}

// Namuna amal tugmasi: faqat toast chiqaradi
export function SampleButton({ children, variant = 'secondary', size = 'sm', ...rest }) {
  return (
    <Button {...rest} variant={variant} size={size} onClick={sampleAction}>
      {children}
    </Button>
  );
}

export function SampleActions({ actions, size = 'sm' }) {
  if (!actions || (Array.isArray(actions) && !actions.length)) return null;
  const list = Array.isArray(actions) ? actions : [actions];
  return (
    <div className="ui-cab-actions">
      {list.map((action, index) =>
        React.isValidElement(action) ? (
          <React.Fragment key={index}>{action}</React.Fragment>
        ) : (
          <SampleButton key={`${action}-${index}`} size={size} variant={index === 0 && list.length > 1 ? 'primary' : 'secondary'}>
            {action}
          </SampleButton>
        )
      )}
    </div>
  );
}

// Sarlavha + "Namuna" belgisi
export function SectionTitle({ title, sample }) {
  if (!title && !sample) return null;
  return (
    <span className="ui-cab-title">
      {title && <span>{title}</span>}
      {sample && <SampleBadge />}
    </span>
  );
}

// Karta: sarlavha, namuna belgisi, amallar
export function SectionCard({ title, subtitle, sample, actions, padding = 'md', className, children }) {
  return (
    <Card
      className={cx('ui-cab-card', className)}
      padding={padding}
      title={title || sample ? <SectionTitle title={title} sample={sample} /> : undefined}
      subtitle={subtitle}
      actions={actions ? <SampleActions actions={actions} /> : undefined}
    >
      {children}
    </Card>
  );
}

// Statistika kartasi. error bo'lsa qiymat "—" (namuna qo'yilmaydi).
export function StatTile({ label, value, trend, icon, tone, sample, loading, error, hint }) {
  return (
    <StatCard
      className="ui-cab-stat"
      data-testid={typeof label === 'string' ? `stat:${label}` : undefined}
      icon={icon}
      tone={tone}
      trend={error ? undefined : trend}
      loading={loading}
      label={<SectionTitle title={label} sample={sample} />}
      value={
        error ? (
          <span className="ui-cab-stat__error" title="Ma'lumotni yuklab bo'lmadi">
            {DASH}
          </span>
        ) : (
          <>
            {value === null || value === undefined || value === '' ? DASH : value}
            {hint && <span className="ui-cab-stat__hint">{hint}</span>}
          </>
        )
      }
    />
  );
}

export function StatsGrid({ items, sample }) {
  return (
    <div className="ui-cab-stats">
      {items.map((item, index) => (
        <StatTile key={`${typeof item.label === 'string' ? item.label : index}`} sample={item.sample ?? sample} {...item} />
      ))}
    </div>
  );
}

// Haqiqiy ma'lumot xatosi
export function ErrorNote({ error, onRetry }) {
  const status = error?.response?.status;
  return (
    <div className="ui-cab-error" role="alert">
      <AlertTriangle aria-hidden="true" />
      <div className="ui-cab-error__text">
        <strong>Ma'lumotni yuklab bo'lmadi</strong>
        <span>{status === 403 ? "Bu ma'lumotni ko'rish uchun ruxsat yo'q (403)." : status ? `Server javobi: ${status}` : error?.message || ''}</span>
      </div>
      {onRetry && (
        <Button size="sm" variant="secondary" onClick={onRetry}>
          Qayta urinish
        </Button>
      )}
    </div>
  );
}

export function InfoNote({ children, tone = 'info' }) {
  return (
    <div className={cx('ui-cab-note', `ui-cab-note--${tone}`)}>
      <Info aria-hidden="true" />
      <span>{children}</span>
    </div>
  );
}

export function ProgressCell({ value }) {
  if (value === null || value === undefined || Number.isNaN(Number(value))) return <span className="ui-cab-muted">{DASH}</span>;
  const percent = Math.round(Number(value));
  return (
    <div className="ui-cab-progress">
      <ProgressBar value={percent} label={`${percent}%`} />
      <span className="ui-cab-progress__value">{percent}%</span>
    </div>
  );
}
